/**
 * Карточка блока-таблицы: строки показателей, которых на территории может быть
 * сколько угодно — продукты, потребности населения, сёла.
 *
 * Строки добавляются и удаляются оператором. Блок сохраняется целиком, поэтому
 * удаление строки здесь означает удаление строки в базе — об этом говорит
 * подпись у кнопки, а не только иконка.
 *
 * Исключение — блок с КАНОНИЧЕСКИМ набором строк (`fixed_rows`, например
 * самообеспеченность продуктами). Там строки задаёт справочник: их не добавляют
 * и не удаляют, а колонки-личности (`readonly`) показываются текстом. Раньше
 * название продукта вводилось руками, и опечатка делала строку невидимой для
 * витрины — она сопоставляет по названию.
 */
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { cn } from '../../../../lib/cn';
import type { PassportBlock } from '../../../../requests/input_data.request';
import { pick, type BlockRow, type Lang } from '../formState';
import { inputTypeOf } from './FieldsCard';

export interface BlockCardProps {
  block: PassportBlock;
  lang: Lang;
  rows: BlockRow[];
  /** Блок изменён — подсвечиваем заголовок, чтобы правку было видно свёрнутой. */
  changed: boolean;
  onCellChange: (uid: string, column: string, value: string) => void;
  onAddRow: () => void;
  onRemoveRow: (uid: string) => void;
  manual: boolean;
  onToggleManual: (manual: boolean) => void;
  labels: {
    addRow: string;
    removeRow: string;
    empty: string;
    source: string;
    sourceSnp: string;
    sourceManual: string;
  };
}

export const BlockCard = ({
  block,
  lang,
  rows,
  changed,
  onCellChange,
  onAddRow,
  onRemoveRow,
  manual,
  onToggleManual,
  labels,
}: BlockCardProps) => (
  <Card className="shadow-card" data-testid={`block-${block.id}`}>
    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-3">
      <h3 className={cn('text-base font-semibold', changed && 'text-brand-700')}>
        {pick(block.label_ru, block.label_kz, lang)}
      </h3>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-xs tabular-nums text-text-muted">{rows.length}</span>
        {block.snp_source && (
          <select
            value={manual ? 'manual' : 'source'}
            aria-label={labels.source}
            data-testid={`source-${block.id}`}
            onChange={(event) => onToggleManual(event.target.value === 'manual')}
            className="h-9 rounded-md border border-surface-border bg-white px-2 text-xs"
          >
            <option value="source">{labels.sourceSnp}</option>
            <option value="manual">{labels.sourceManual}</option>
          </select>
        )}
      </div>
    </CardHeader>

    <CardContent className="pt-0">
      {rows.length === 0 ? (
        <p className="mb-3 text-sm text-text-muted">{labels.empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border text-left text-xs text-text-muted">
                {block.columns.map((column) => (
                  <th key={column.column} className="p-2 font-medium">
                    {pick(column.label_ru, column.label_kz, lang)}
                    {column.unit !== null && (
                      <span className="block font-normal">{column.unit}</span>
                    )}
                  </th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.uid} className="border-b border-surface-border last:border-0">
                  {block.columns.map((column) => (
                    <td key={column.column} className="px-2 py-1.5">
                      {column.readonly ? (
                        <span
                          className="block min-w-28 py-2 text-sm"
                          data-testid={`cell-${block.id}-${row.uid}-${column.column}`}
                        >
                          {row.cells[column.column] ?? ''}
                        </span>
                      ) : column.dtype === 'bool' ? (
                        <input
                          type="checkbox"
                          checked={row.cells[column.column] === 'true'}
                          disabled={!manual}
                          aria-label={pick(column.label_ru, column.label_kz, lang)}
                          data-testid={`cell-${block.id}-${row.uid}-${column.column}`}
                          onChange={(event) =>
                            onCellChange(
                              row.uid,
                              column.column,
                              event.target.checked ? 'true' : 'false',
                            )
                          }
                          className="size-5"
                        />
                      ) : (
                        <Input
                          type={inputTypeOf(column.dtype)}
                          inputMode={column.dtype === 'str' ? undefined : 'decimal'}
                          value={row.cells[column.column] ?? ''}
                          disabled={!manual}
                          aria-label={pick(column.label_ru, column.label_kz, lang)}
                          data-testid={`cell-${block.id}-${row.uid}-${column.column}`}
                          onChange={(event) =>
                            onCellChange(row.uid, column.column, event.target.value)
                          }
                          className={cn(
                            'h-9 w-full min-w-28',
                            column.dtype === 'str' ? 'text-left' : 'text-right tabular-nums',
                            !manual && 'bg-surface-muted text-text-muted',
                          )}
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-1">
                    {!block.fixed_rows && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        disabled={!manual}
                        title={labels.removeRow}
                        aria-label={labels.removeRow}
                        data-testid={`remove-${block.id}-${row.uid}`}
                        onClick={() => onRemoveRow(row.uid)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!block.fixed_rows && (
        <Button
          type="button"
          variant="ghost"
          disabled={!manual}
          data-testid={`add-${block.id}`}
          onClick={onAddRow}
          className="mt-2"
        >
          <Plus className="mr-2 size-4" />
          {labels.addRow}
        </Button>
      )}
    </CardContent>
  </Card>
);
